// @ts-check

import React, { useEffect, useMemo, useState } from "react";
import { BlockStack, Layout, Page } from "@shopify/polaris";
import { useNavigate, useParams } from "react-router-dom";
import { keepPreviousData } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";

import { AdCampaignStatus } from "components/AdCampaignStatus/AdCampaignStatus";
import { FetchOrdersControls } from "components/FetchOrderControls/FetchOrdersControls";
import { AdPropertyStats } from "components/AdPropertyStats/AdPropertyStats";
import { AdSetsTable } from "components/AdSetsTable/AdSetsTable";

import { useGlobalContext } from "context/GlobalContext";

import { useAppQuery } from "hooks/use-app-query";
import { useFormatCurrency } from "hooks/use-format-currency";

import { AD_CAMPAIGNS_PAGE_SIZE } from "utils/ad-campaigns";

export default function AdCampaignViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const posthog = usePostHog();
  const { dates } = useGlobalContext();
  const formatCurrency = useFormatCurrency();

  const [adSetsPage, setAdSetsPage] = useState(1);

  useEffect(() => {
    posthog.capture("$pageview");
  }, []);

  const {
    data: adCampaignResponse,
    refetch: refetchAdCampaign,
    isLoading: isLoadingAdCampaign,
    isFetching: isFetchingAdCampaign,
    isRefetching: isRefetchingAdCampaign,
  } = useAppQuery({
    url: `/api/ad-campaigns/${id}`,
    extraQueryKeys: ["adCampaigns"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const isInitialLoadingAdCampaign = isLoadingAdCampaign || (isFetchingAdCampaign && !adCampaignResponse);

  const adCampaignStatsUrl = useMemo(() => {
    const params = {
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    return `/api/ad-campaigns/${id}/stats?${new URLSearchParams(params).toString()}`;
  }, [dates]);

  const {
    data: adCampaignStatsResponse,
    refetch: refetchAdCampaignStats,
    isLoading: isLoadingAdCampaignStats,
    isFetching: isFetchingAdCampaignStats,
    isRefetching: isRefetchingAdCampaignStats,
  } = useAppQuery({
    url: adCampaignStatsUrl,
    extraQueryKeys: ["ads"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const isInitialLoadingAdCampaignStats = isLoadingAdCampaignStats || (isFetchingAdCampaignStats && !adCampaignStatsResponse);

  const adSetsUrl = useMemo(() => {
    const params = {
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page: adSetsPage,
      page_size: AD_CAMPAIGNS_PAGE_SIZE,
      platform_key: "facebook",
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    return `/api/ad-campaigns/${id}/ad-sets?${new URLSearchParams(params).toString()}`;
  }, [dates, adSetsPage]);

  const {
    data: adSetsResponse,
    refetch: refetchAdSets,
    isFetching: isFetchingAdSets,
    isRefetching: isRefetchingAdSets,
  } = useAppQuery({
    url: adSetsUrl,
    extraQueryKeys: ["adCampaigns"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const adSets = adSetsResponse?.adSets || [];

  const adCampaign = adCampaignResponse?.adCampaign;
  const totalAdCampaignStats = adCampaignStatsResponse?.totalAdCampaignStats || {};

  let status;
  if (adCampaign) {
    status = (
      <AdCampaignStatus status={adCampaign.status} />
    );
  }

  let title;
  if (isInitialLoadingAdCampaign) {
    title = "Loading...";
  } else if (adCampaign) {
    title = adCampaign.name;
  }

  const handleRefresh = () => {
    refetchAdCampaign();
    refetchAdCampaignStats();
    refetchAdSets();
  };

  return (
    <Page
      title={title}
      titleMetadata={status}
      subtitle={adCampaign ? adCampaign.platform_accounts.connected_platforms.connectable_platforms.name : undefined}
      backAction={{
        content: "Back to Ad Campaigns",
        onAction: () => {
          navigate("/ad-campaigns");
        },
      }}
      fullWidth
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <FetchOrdersControls
              onRefresh={handleRefresh}
              loading={isRefetchingAdCampaignStats || isRefetchingAdCampaign}
            />

            <AdPropertyStats
              loading={isInitialLoadingAdCampaignStats}
              totalSpend={totalAdCampaignStats.total_spend ? formatCurrency(totalAdCampaignStats.total_spend) : formatCurrency(0)}
              totalRevenue={totalAdCampaignStats.total_revenue ? formatCurrency(totalAdCampaignStats.total_revenue) : formatCurrency(0)}
              totalClicks={totalAdCampaignStats.total_clicks || 0}
              totalOrders={totalAdCampaignStats.total_orders || 0}
              roas={totalAdCampaignStats.avg_roas ? `${totalAdCampaignStats.avg_roas.toFixed(1)}x` : "0x"}
              cac={totalAdCampaignStats.avg_cac ? formatCurrency(totalAdCampaignStats.avg_cac) : formatCurrency(0)}
              aov={totalAdCampaignStats.avg_aov ? formatCurrency(totalAdCampaignStats.avg_aov) : formatCurrency(0)}
              impressions={totalAdCampaignStats.total_impressions || 0}
              conversionRate={totalAdCampaignStats.avg_conversion_rate ? `${(totalAdCampaignStats.avg_conversion_rate * 100).toFixed(1)}%` : "0%"}
              newCustomers={totalAdCampaignStats.total_new_customers || 0}
            />

            <AdSetsTable
              adSets={adSets}
              loading={isFetchingAdSets || isRefetchingAdSets}
              page={adSetsPage}
              setPage={setAdSetsPage}
            />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
